import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ArrowRightLeft,
  Copy,
  Check,
  Download,
  FilterX,
  Settings2,
  ChevronsUpDown,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { Transmitter } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range' | 'searchable-select';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers?: TransmitterRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

export interface TransmitterRowHelpers {
  onEdit?: (transmitter: Transmitter) => void;
  onReassign?: (transmitter: Transmitter) => void;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getOrganisationName(org: Transmitter['organisation']): string {
  if (!org) return '';
  if (typeof org === 'string') return org;
  return org.name || '';
}

function getAccountId(org: Transmitter['organisation']): string | null {
  if (!org || typeof org === 'string') return null;
  return org.accountId || null;
}

function getSiteName(site: Transmitter['site']): string {
  if (!site) return '';
  if (typeof site === 'string') return site;
  return site.name || '';
}

function getLocationName(location: Transmitter['location']): string {
  if (!location) return '';
  if (typeof location === 'string') return location;
  return location.name || '';
}

function getStreamCount(streams: Transmitter['streams']): number {
  if (!streams) return 0;
  return streams.length;
}

function getRssiLabel(rssi: number | undefined): string {
  if (rssi === undefined || rssi === null) return '-';
  if (rssi >= 6) return 'Excellent';
  if (rssi >= 4) return 'Good';
  if (rssi >= 2) return 'Fair';
  if (rssi >= 1) return 'Poor';
  return 'None';
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0"
      onClick={handleCopy}
      aria-label={label}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </Button>
  );
}

// =============================================================================
// Column Definitions
// =============================================================================

export const COLUMNS: ColumnDef<Transmitter>[] = [
  {
    key: 'dev_id',
    label: 'Product ID',
    sortKey: 'dev_id',
    filterType: 'text',
    getValue: (t) => t.dev_id,
    render: (t) => (
      <div className="flex items-center gap-1">
        <span className="font-mono text-xs font-medium">{t.dev_id}</span>
        <CopyButton text={t.dev_id} label={`Copy product ID: ${t.dev_id}`} />
      </div>
    ),
  },
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (t) => t.name || '',
    render: (t) =>
      t.name ? (
        <span className="font-medium">{t.name}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'account',
    label: 'Account',
    filterType: 'searchable-select',
    getValue: (t) => getOrganisationName(t.organisation),
    render: (t) => {
      const accountId = getAccountId(t.organisation);
      const orgName = getOrganisationName(t.organisation);
      return accountId ? (
        <Link
          to={`/accounts/${accountId}`}
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {orgName}
        </Link>
      ) : orgName ? (
        <span>{orgName}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      );
    },
  },
  {
    key: 'sensors',
    label: 'Sensors',
    getValue: (t) => getStreamCount(t.streams),
    render: (t) => {
      const count = getStreamCount(t.streams);
      return (
        <span className={count === 0 ? 'text-muted-foreground' : ''}>
          {count}
        </span>
      );
    },
    align: 'center',
  },
  {
    key: 'site',
    label: 'Site',
    filterType: 'text',
    getValue: (t) => getSiteName(t.site),
    render: (t) => {
      const siteName = getSiteName(t.site);
      const locName = getLocationName(t.location);
      return (
        <div className="text-sm">
          {siteName || <span className="text-muted-foreground">&mdash;</span>}
          {locName && (
            <span className="text-muted-foreground"> / {locName}</span>
          )}
        </div>
      );
    },
  },
  {
    key: 'lastPacket',
    label: 'Last Packet',
    sortKey: 'lastpacket_at',
    filterType: 'date-range',
    getValue: (t) =>
      t.lastpacket_at
        ? new Date(t.lastpacket_at).toLocaleDateString('en-CA')
        : '',
    render: (t) => formatDate(t.lastpacket_at),
  },
  {
    key: 'firmware',
    label: 'Firmware',
    filterType: 'text',
    getValue: (t) => t.current_firmware_name || '',
    defaultVisible: true,
    render: (t) =>
      t.current_firmware_name ? (
        <span className="text-xs">{t.current_firmware_name}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'rssi',
    label: 'RSSI',
    getValue: (t) => t.rssi ?? null,
    render: (t) => {
      const rssi = t.rssi;
      if (rssi === undefined || rssi === null)
        return <span className="text-muted-foreground">&mdash;</span>;
      const label = getRssiLabel(rssi);
      const color =
        rssi >= 4
          ? 'text-green-600'
          : rssi >= 2
            ? 'text-yellow-600'
            : 'text-red-600';
      return (
        <span className={`text-xs font-medium ${color}`}>
          {rssi} ({label})
        </span>
      );
    },
    align: 'center',
  },
  {
    key: 'battery',
    label: 'Battery',
    getValue: (t) =>
      t.battery_percentage !== null && t.battery_percentage !== undefined
        ? Number(t.battery_percentage)
        : t.battery_voltage !== null && t.battery_voltage !== undefined
          ? Number(t.battery_voltage)
          : null,
    render: (t) => {
      const pct = t.battery_percentage;
      const volts = t.battery_voltage;
      if (pct !== null && pct !== undefined) {
        const num = Number(pct);
        const color =
          num >= 50
            ? 'text-green-600'
            : num >= 20
              ? 'text-yellow-600'
              : 'text-red-600';
        return <span className={`text-xs font-medium ${color}`}>{Math.round(num)}%</span>;
      }
      if (volts !== null && volts !== undefined) {
        const v = Number(volts);
        const color =
          v > 3.4
            ? 'text-green-600'
            : v > 2.9
              ? 'text-yellow-600'
              : 'text-red-600';
        return <span className={`text-xs font-medium ${color}`}>{v.toFixed(2)}V</span>;
      }
      return <span className="text-muted-foreground">&mdash;</span>;
    },
    align: 'center',
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: [
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ],
    getValue: (t) => String(t.active_flag),
    render: (t) =>
      t.active_flag ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Active
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Inactive
        </span>
      ),
  },
  // Hidden by default columns
  {
    key: 'xbee_SL',
    label: 'Xbee Serial',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => t.xbee_SL || '',
    render: (t) =>
      t.xbee_SL ? (
        <span className="font-mono text-xs">{t.xbee_SL}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'ICCID',
    label: 'ICCID',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => t.ICCID || '',
    render: (t) =>
      t.ICCID ? (
        <span className="font-mono text-xs">{t.ICCID}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'IMEI',
    label: 'IMEI',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => t.IMEI || '',
    render: (t) =>
      t.IMEI ? (
        <span className="font-mono text-xs">{t.IMEI}</span>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'simprovider',
    label: 'SIM Provider',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => t.simprovider || '',
  },
  {
    key: 'location',
    label: 'Location',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => getLocationName(t.location),
  },
  {
    key: 'description',
    label: 'Description',
    defaultVisible: false,
    filterType: 'text',
    getValue: (t) => t.description || '',
  },
  {
    key: 'installedAt',
    label: 'Installed At',
    defaultVisible: false,
    filterType: 'date-range',
    getValue: (t) =>
      t.installed_at
        ? new Date(t.installed_at).toLocaleDateString('en-CA')
        : '',
    render: (t) => formatDate(t.installed_at),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    defaultVisible: false,
    sortKey: 'created_at',
    filterType: 'date-range',
    getValue: (t) =>
      t.created_at
        ? new Date(t.created_at).toLocaleDateString('en-CA')
        : '',
    render: (t) => formatDate(t.created_at),
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (t, helpers) => (
      <div className="flex items-center gap-2">
        {helpers?.onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              helpers.onEdit?.(t);
            }}
            className="text-atek-blue hover:underline text-sm cursor-pointer"
          >
            Edit
          </button>
        )}
        {helpers?.onReassign && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              helpers.onReassign?.(t);
            }}
            className="text-muted-foreground hover:text-foreground cursor-pointer"
            title="Reassign to another account"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
        )}
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// Column Visibility Helpers
// =============================================================================

const DEFAULT_VISIBILITY_KEY = 'atek-transmitters-column-visibility';

function getDefaultVisibility(columns: ColumnDef<Transmitter>[] = COLUMNS): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of columns) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

export function loadColumnVisibility(
  storageKey: string = DEFAULT_VISIBILITY_KEY,
  columns: ColumnDef<Transmitter>[] = COLUMNS,
): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return { ...getDefaultVisibility(columns), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility(columns);
}

export function saveColumnVisibility(
  visibility: Record<string, boolean>,
  storageKey: string = DEFAULT_VISIBILITY_KEY,
) {
  localStorage.setItem(storageKey, JSON.stringify(visibility));
}

// =============================================================================
// SearchableSelectFilter (Combobox with search + multi-select)
// =============================================================================

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(search.toLowerCase()))
      : options;
    if (selected.length === 0) return base;
    const sel = new Set(selected);
    return [...base].sort((a, b) => (sel.has(a.value) ? 0 : 1) - (sel.has(b.value) ? 0 : 1));
  }, [options, search, selected]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs justify-between font-normal"
          >
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10"
            onClick={(e) => {
              e.stopPropagation();
              onChange(colKey, '');
              setOpen(false);
            }}
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              No results
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(opt.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Searchable-select filter (combobox with search)
  if (col.filterType === 'searchable-select') {
    const options = searchableSelectOptions?.[col.key] || [];
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SearchableSelectFilter
          colKey={col.key}
          options={options}
          value={columnFilters[col.key] || ''}
          onChange={setColumnFilter}
        />
      </TableHead>
    );
  }

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key]
          ? JSON.parse(columnFilters[col.key])
          : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${to}`)
            }
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) =>
              setColumnFilter(col.key, `${from}>>${e.target.value}`)
            }
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${max}`)
            }
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) =>
              setColumnFilter(col.key, `${min}>>${e.target.value}`)
            }
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Table Component
// =============================================================================

interface TransmittersTableProps {
  transmitters: Transmitter[];
  isLoading: boolean;
  total: number;
  sortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;
  columnVisibility: Record<string, boolean>;
  toggleColumn: (key: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onExportCSV: () => void;
  onCopyClipboard: () => void;
  columns?: ColumnDef<Transmitter>[];
  emptyMessage?: string;
  onEdit?: (transmitter: Transmitter) => void;
  onReassign?: (transmitter: Transmitter) => void;
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>;
  onClearColumnFilters?: () => void;
}

export function TransmittersTable({
  transmitters,
  isLoading,
  total,
  sortKey,
  sortDirection,
  onSort,
  columnFilters,
  setColumnFilter,
  columnVisibility,
  toggleColumn,
  page,
  totalPages,
  onPageChange,
  onExportCSV,
  onCopyClipboard,
  columns: columnsProp = COLUMNS,
  emptyMessage = 'No transmitters found',
  onEdit,
  onReassign,
  searchableSelectOptions,
  onClearColumnFilters,
}: TransmittersTableProps) {
  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);
  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = columnsProp.filter((col) => isVisible(col.key));

  const rowHelpers: TransmitterRowHelpers | undefined =
    onEdit || onReassign
      ? { onEdit, onReassign }
      : undefined;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {transmitters.length} of {total}
          </span>
          <div className="flex items-center gap-2">
            {hasActiveColumnFilters && onClearColumnFilters && (
              <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
                <FilterX className="h-4 w-4 mr-1" /> Clear filters
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onCopyClipboard}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columnsProp.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Filter inputs */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter, searchableSelectOptions),
                )}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(col.align === 'right' && 'text-right')}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {/* Empty state */}
              {!isLoading && transmitters.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading &&
                transmitters.map((transmitter) => (
                  <TableRow
                    key={transmitter._id}
                    className="hover:bg-muted/50"
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(transmitter, rowHelpers)
                          : String(col.getValue(transmitter) ?? '') || '\u2014'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
